import React from 'react';
import { Link } from 'react-router-dom';

import { fmt, TEAL } from '@/features/website-dashboard/lib';

/**
 * Small presentational atoms shared across the Website Dashboard tiles and
 * tables. Styling is inline to match the design's palette exactly and to stay
 * independent of the admin's dark-mode tokens (this page is a light surface).
 */

/** Uppercase section label with a hairline rule, e.g. "Site health". */
export const SectionHeading = ({
  title,
  right,
}: {
  title: string;
  right?: React.ReactNode;
}): React.JSX.Element => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
    <div
      style={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '.12em',
        textTransform: 'uppercase',
        color: '#6b7075',
        whiteSpace: 'nowrap',
      }}
    >
      {title}
    </div>
    <div style={{ flex: 1, height: 1, background: '#e3e5e5' }} />
    {right}
  </div>
);

/** White rounded card with the design's hairline border. */
export const Card = ({
  children,
  style,
  id,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
  id?: string;
}): React.JSX.Element => (
  <div
    id={id}
    style={{
      background: '#fff',
      border: '1px solid #e3e5e5',
      borderRadius: 16,
      overflow: 'hidden',
      ...style,
    }}
  >
    {children}
  </div>
);

/** Soft status / label chip. */
export const Chip = ({
  bg,
  fg,
  children,
}: {
  bg: string;
  fg: string;
  children: React.ReactNode;
}): React.JSX.Element => (
  <span
    style={{
      display: 'inline-block',
      fontSize: 11,
      fontWeight: 700,
      borderRadius: 5,
      padding: '3px 8px',
      background: bg,
      color: fg,
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </span>
);

/**
 * A number in a table cell. A zero is faded (an empty cell reads as "missing");
 * a non-zero number with `to` links into the list it counts.
 */
export const Num = ({
  value,
  strong,
  to,
}: {
  value: number;
  strong?: boolean;
  to?: string;
}): React.JSX.Element => {
  const color = value === 0 ? '#c5c9cb' : strong ? '#17171a' : '#2a2e32';
  const weight = strong ? 700 : 500;
  if (!to || value === 0) {
    return <span style={{ color, fontWeight: weight }}>{fmt(value)}</span>;
  }
  return (
    <Link
      to={to}
      title="Open the list (opens in a new tab)"
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: value === 0 ? color : TEAL, fontWeight: weight }}
    >
      {fmt(value)}
    </Link>
  );
};

/** Card body title row used by the smaller breakdown sub-tables. */
export const SubTableHead = ({ children }: { children: React.ReactNode }): React.JSX.Element => (
  <div
    style={{
      padding: '9px 12px',
      fontSize: 13,
      fontWeight: 700,
      borderBottom: '1px solid #eef0f0',
    }}
  >
    {children}
  </div>
);
